// WHAT THE SELECTION IS MADE OF, and the button that turns one part
// group into its own tracked category.
//
// Owner, 2026-09-24: "select specific assembly subcomponents and use
// that for count and length ... a button next to each sub component /
// assembly part, that can create a subcategory for tracking".
//
// It takes its payload as a PROP rather than fetching: the page has
// already asked the server for this selection, and taking that answer
// instead of asking again is what makes one number appear in every
// place by construction. Same reason SelectionDetails takes `summary`.
//
// THE CREATE BUTTON IS DISABLED UNTIL THE TOTALS RULE IS KNOWN, and
// that is deliberate rather than unfinished. If Beam Clamps (from
// Hangers) is tracked while Hangers is also tracked, a naive total
// counts the clamps twice. HAL asked that we not ship a button that
// creates that silently; a promise to add a warning later is exactly
// how it would ship silently. So the component cannot create anything
// until it is given the rule in words, and it says why.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Modal, Input, Select, Table, Tooltip } from "antd";
import { popupInStage, stageContainer } from "./stagePopup";
import { compact, exact } from "./quantityFormat";
import {
  amountOf, missingCountOf, unitsOf, truncatedText, measureColumnsOf, absentText,
  noMeasuresNote, sortGroups, groupKey, defaultSubcategoryName, coverageText,
  progressMoveText, refusalOf, candidateOf, collapsedSummary,
} from "./assemblyGroups";

const T = { muted: "#6B7280", ink: "#111827" };
const MEASURE_LABEL = { count: "Count", length: "Length", area: "Area", volume: "Volume" };

export default function AssemblyGroups({
  payload = null,
  sourceCategory = "",
  units = {},
  // The rule, in Mark's words, about how a derived category meets its
  // parent's totals. No rule, no creating.
  totalsNote = "",
  // HOW STATUS WORKS, from the server (GET /derived-categories
  // statusMode) rather than assumed here. Shown only when we were told:
  // a panel that states the mode it GUESSED is worse than one that says
  // nothing, because a person would act on it.
  statusMode = "",
  // Mark's dry run, supplied by the page: given a candidate definition
  // it answers with the movement and any refusal, WITHOUT writing. The
  // panel does not know the endpoint; it knows there is one.
  onPreview = null,
  onCreate = null,
  loading = false,
  // SHIPS CLOSED. The panel reads as a narrowing and this section keeps
  // its place in it, but the Elements list below is where status gets
  // assigned - constantly - so it must not be pushed off the screen by
  // a section somebody opens occasionally.
  startOpen = false,
}) {
  const [open, setOpen] = useState(null);      // the group being turned into a category
  const [name, setName] = useState("");
  const [measure, setMeasure] = useState("count");
  const [open_, setOpen_] = useState(!!startOpen);
  const [preview, setPreview] = useState(null);
  // The name at ask time, without making the preview re-run on every
  // keystroke: the server does not price a definition by its name.
  const nameRef = useRef("");
  const [previewing, setPreviewing] = useState(false);
  const askSeq = useRef(0);

  const groups = useMemo(() => sortGroups((payload && payload.groups) || []), [payload]);
  // From the build that wrote the numbers, not the page's preference.
  const measureUnits = unitsOf(payload);
  const truncated = truncatedText(payload);
  const columns = useMemo(() => measureColumnsOf(groups), [groups]);
  const note = noMeasuresNote(groups);
  const coverage = coverageText(payload);
  const canCreate = !!(onCreate && totalsNote);

  const start = (g) => {
    setOpen(g);
    const n = defaultSubcategoryName(g, sourceCategory);
    setName(n);
    nameRef.current = n;
    // Count is always available; a measure is only offered when the
    // model actually carries it for that group.
    setMeasure("count");
    setPreview(null);
  };

  // Ask what it WOULD do, whenever the candidate changes. Sequenced, so
  // a slow answer for an abandoned candidate cannot overwrite a fresh
  // one - the panel would otherwise show the movement for a measure the
  // person has already changed away from.
  const ask = useCallback(async (group, m) => {
    if (!onPreview || !group) return;
    const seq = (askSeq.current += 1);
    setPreviewing(true);
    try {
      const r = await onPreview(candidateOf({ group, name: nameRef.current, measure: m, sourceCategory }));
      if (seq === askSeq.current) setPreview(r || null);
    } catch (e) {
      // A PREVIEW THAT FAILED IS NOT A PREVIEW THAT SAID "NO MOVEMENT".
      if (seq === askSeq.current) setPreview({ err: "The effect on progress could not be worked out." });
    } finally {
      if (seq === askSeq.current) setPreviewing(false);
    }
  }, [onPreview, sourceCategory]);

  useEffect(() => { if (open) ask(open, measure); }, [open, measure, ask]);

  const measuresFor = (g) => ["count", ...["length", "area", "volume"].filter((k) => amountOf(g, k) !== null)];

  const cols = [
    { title: "Part", key: "part", render: (_, g) => (
        <span style={{ color: T.ink }}>
          {[g.partFamily, g.partType].filter(Boolean).join("  ")}
          <span style={{ color: T.muted }}>{g.partCategory ? `  ·  ${g.partCategory}` : ""}</span>
        </span>
      ) },
    { title: "Count", key: "count", width: 90, align: "right",
      render: (_, g) => <Tooltip getPopupContainer={popupInStage} title={`${Number(g.totalCount || 0).toLocaleString()} in the selection`}>{compact(g.totalCount)}</Tooltip> },
    { title: "Per parent", key: "parents", width: 100, align: "right",
      render: (_, g) => <span style={{ color: T.muted }}>{Number(g.parentCount || 0).toLocaleString()}</span> },
    ...columns.map((k) => ({
      title: MEASURE_LABEL[k], key: k, width: 120, align: "right",
      render: (_, g) => {
        const v = amountOf(g, k);
        // ABSENT IS SAID, NOT SHOWN AS ZERO.
        if (v === null) return <span style={{ color: T.muted, fontStyle: "italic" }}>{absentText(k)}</span>;
        const u = measureUnits[k] || units[k] || "";
        const shown = compact(v);
        const missing = missingCountOf(g, k);
        return (
          <Tooltip getPopupContainer={popupInStage} title={missing ? `${exact(v, u)} - ${missing.toLocaleString()} of these parts have no ${k} in the model` : exact(v, u)}>
            <span>
              {shown}
              {missing ? <span style={{ color: T.muted }}> *</span> : null}
            </span>
          </Tooltip>
        );
      },
    })),
    { title: "", key: "act", width: 150, render: (_, g) => (
        <Tooltip getPopupContainer={popupInStage} title={canCreate ? "" : "Cannot create yet: how a derived category meets its parent's totals has not been settled."}>
          <Button size="small" disabled={!canCreate} onClick={() => start(g)}>Track as subcategory</Button>
        </Tooltip>
      ) },
  ];

  if (!loading && !groups.length) {
    return <Empty description="The selection has no assembly parts" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const summary = collapsedSummary(payload);
  if (!open_) {
    return (
      <div className="bl-assembly-groups">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* The closed line carries the two numbers that say whether
              opening it is worth it - not a bare triangle. */}
          <span style={{ color: T.muted }}>{summary || "No assembly parts"}</span>
          {summary
            ? <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setOpen_(true)}>Show</Button>
            : null}
        </div>
      </div>
    );
  }

  return (
    <div className="bl-assembly-groups">
      <div style={{ marginBottom: 6 }}>
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setOpen_(false)}>Hide</Button>
      </div>
      {coverage ? <div style={{ color: T.muted, marginBottom: 6 }}>{coverage}</div> : null}
      {note ? <div style={{ color: T.muted, marginBottom: 6 }}>{note}</div> : null}
      {truncated ? <div style={{ color: T.muted, marginBottom: 6 }}>{truncated}</div> : null}
      <Table getPopupContainer={popupInStage}
        size="small"
        rowKey={groupKey}
        loading={loading}
        columns={cols}
        dataSource={groups}
        pagination={groups.length > 20 ? { pageSize: 20, size: "small" } : false}
      />
      <Modal
        getContainer={stageContainer}
        open={!!open}
        title={open ? `Track ${[open.partFamily, open.partType].filter(Boolean).join(" ")} as its own category` : ""}
        okText="Create"
        okButtonProps={{ disabled: !name.trim() || previewing || !!refusalOf(preview) }}
        onCancel={() => setOpen(null)}
        onOk={() => {
          if (!open || !onCreate) return;
          // EXACTLY what the preview was asked about, so the two cannot
          // answer different questions.
          onCreate(candidateOf({ group: open, name: name.trim(), measure, sourceCategory }));
          setOpen(null);
        }}
      >
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: T.muted, marginBottom: 4 }}>Name</div>
          <Input value={name} onChange={(e) => { setName(e.target.value); nameRef.current = e.target.value; }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: T.muted, marginBottom: 4 }}>Track by</div>
          <Select getPopupContainer={popupInStage} value={measure} onChange={setMeasure} style={{ width: 200 }}
            options={open ? measuresFor(open).map((k) => ({ value: k, label: MEASURE_LABEL[k] })) : []} />
          {open && measuresFor(open).length === 1 ? (
            <div style={{ color: T.muted, marginTop: 4 }}>
              Only count is available - {absentText("length")}.
            </div>
          ) : null}
        </div>
        {/* THE RULE, IN THE SERVER'S WORDS, shown BEFORE the person
            creates the category rather than discovered afterwards. It is
            a server field and not a string in this file for a reason:
            the owner changed its meaning twice in two hours on the day
            it was written, and the screen must not be able to drift
            from the numbers. */}
        <div style={{ color: T.muted, borderTop: "1px solid #E5E7EB", paddingTop: 8 }}>
          <div>{totalsNote}</div>
          {/* WHAT IT WOULD DO, before it is done. */}
          {previewing ? <div style={{ marginTop: 4 }}>Working out the effect on progress…</div> : null}
          {!previewing && refusalOf(preview)
            ? <div style={{ marginTop: 4, color: "#B42318" }}>{refusalOf(preview)}</div>
            : null}
          {!previewing && !refusalOf(preview) && progressMoveText(preview)
            ? <div style={{ marginTop: 4, color: T.ink }}>{progressMoveText(preview)}</div>
            : null}
          {statusMode === "parent" ? (
            <div style={{ marginTop: 4 }}>
              These parts follow their parent&apos;s status - they are not ticked on their own.
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
